// src/components/auth/Register.js

import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth'; // Assuming useAuth will also provide a 'register' function

function RegisterComponent() {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  // IMPORTANT: You'll need to ensure your useAuth hook exports a 'register' function
  // similar to how it exports 'login'.
  // const { login, register } = useAuth();
  const auth = useAuth(); // Get the whole auth context

  const from = location.state?.from?.pathname || "/dashboard"; // Where to redirect after successful login

  const validateForm = () => {
    if (!username || !email || !password || !confirmPassword) {
      setError('All fields are required');
      return false;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    // You might want to add more validations (e.g., email format, password strength)
    setError('');
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    setError('');

    try {
      // 1. Attempt to register the user
      // Ensure your auth.register function exists and handles the backend call
      if (!auth.register) {
        throw new Error("Register function is not available on auth context. Please implement it in useAuth.");
      }
      await auth.register(username, email, password);

      // 2. If registration is successful, automatically log them in
      // Ensure your auth.login function can handle username or email
      if (!auth.login) {
        throw new Error("Login function is not available on auth context. Please implement it in useAuth.");
      }
      // Decide if you log in with username or email based on your login function's expectation
      await auth.login(username, password); // Or auth.login(email, password);

      // 3. Navigate to the intended page or a default protected route
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || 'Registration or login failed!');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Sign Up</h2>
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div>
            <label 
              htmlFor="username"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Username
            </label>
            <input 
              id="username"
              name="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
              placeholder="johndoe"
              required
            />
          </div>

          <div>
            <label 
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Email
            </label>
            <input 
              id="email"
              name="email"
              type="email" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
              placeholder="your@email.com"
              required
            />
          </div>
  
          <div>
            <label 
              htmlFor="password"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Password
            </label>
            <input 
              id="password"
              name="password"
              type="password" 
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
              placeholder="••••••••"
              required
            />
          </div>

          <div>
            <label 
              htmlFor="confirmPassword"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Confirm Password
            </label>
            <input 
              id="confirmPassword"
              name="confirmPassword"
              type="password" // Should be 'password' for masking
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
              placeholder="••••••••"
              required
            />
          </div>
  
          {/* Removed the empty div with remember me styling from the Next.js example */}
  
          <button 
            type="submit"
            disabled={loading}
            className="w-full bg-primary hover:bg-primary-hover text-white font-medium py-2.5 rounded-lg transition-colors disabled:opacity-70" // Matched Login.js button style
          >
            {loading ? 'Signing Up...' : 'Sign Up'}
          </button>
        </div>
      </form>
      {error && <p className="mt-4 text-sm text-center text-red-600">{error}</p>}
  
      <div className="mt-6 text-center text-sm text-gray-600">
        Already have an account? 
        <Link to="/login" className="text-primary hover:text-primary-hover font-medium"> 
          {' '}Sign In
        </Link>
      </div>
    </div>
  );
}

export default RegisterComponent;